import type {
  ObjectToolbarConfig,
  ToolbarActionConfig,
} from '@/blocks/object-toolbar';
import type { ActionItem } from '@/components/actions-button/ActionsButton';

/** Promoted to the toolbar's primary add button rather than the overflow menu. */
const ADD_WIDGET_ID = 'add_widget';
/** Routed to the toolbar's auto-labelled "Download" overflow group. */
const EXPORT_ZIP_ID = 'export_dashboard_images';
/** Id of the single overflow group every dashboard's remaining actions land in. */
const OVERFLOW_GROUP_ID = 'dashboard-actions';

/**
 * Icon per dashboard action id. Every action inside one overflow group needs a
 * distinct icon (no repeats), so these are assigned centrally across both
 * dashboards rather than per page. Ids absent from a page's menu are simply
 * unused.
 */
const ICON_BY_ID: Record<string, ToolbarActionConfig['iconName']> = {
  reset_dashboard: 'file-06',
  save_dashboard: 'save-01',
  save_as_dashboard: 'file-plus-02',
  edit_dashboard: 'pencil-01',
  clear: 'reverse-left',
  [EXPORT_ZIP_ID]: 'download-01',
};

const toToolbarAction = (item: ActionItem): ToolbarActionConfig => ({
  id: item.id,
  label: item.text,
  iconName: ICON_BY_ID[item.id],
  onClick: () => {
    void item.onItemClick();
  },
  disabled: item.disabled,
});

export interface DashboardToolbarConfigArgs {
  /** The page's legacy `ActionsButton` menu items, in their declared order. */
  items: ActionItem[];
  /** Translated page title shown in the toolbar header. */
  title: string;
  /** Translated heading for the overflow group holding the remaining actions. */
  groupLabel: string;
}

/**
 * Map a dashboard's legacy `ActionsButton` menu onto an {@link ObjectToolbarConfig}
 * (CUS-174). Shared by the overall dashboard and the My Items dashboard — both
 * partition the same way, so the mapping lives here rather than being repeated
 * per page:
 *
 * - `add_widget` → the toolbar's primary add button (plus icon), matching the
 *   register create pattern.
 * - `export_dashboard_images` → the `export` slot, which the toolbar flattens
 *   into its auto-labelled "Download" group.
 * - everything else → one overflow group labelled `groupLabel`.
 */
export const buildDashboardToolbarConfig = ({
  items,
  title,
  groupLabel,
}: DashboardToolbarConfigArgs): ObjectToolbarConfig => {
  const addWidgetItem = items.find((item) => item.id === ADD_WIDGET_ID);
  const exportItems = items.filter((item) => item.id === EXPORT_ZIP_ID);
  const menuItems = items.filter(
    (item) => item.id !== EXPORT_ZIP_ID && item.id !== ADD_WIDGET_ID
  );

  return {
    header: { title },
    actions: {
      ...(addWidgetItem
        ? {
            primary: [
              {
                ...toToolbarAction(addWidgetItem),
                iconName: 'plus',
              },
            ],
          }
        : {}),
      ...(menuItems.length > 0
        ? {
            overflow: [
              {
                id: OVERFLOW_GROUP_ID,
                label: groupLabel,
                actions: menuItems.map(toToolbarAction),
              },
            ],
          }
        : {}),
      ...(exportItems.length > 0
        ? {
            export: [
              {
                id: 'export',
                actions: exportItems.map(toToolbarAction),
              },
            ],
          }
        : {}),
    },
  };
};
